"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Check } from "lucide-react";

import type { NavLink, NavSettings, SocialPlatform } from "@/lib/nav";
import { useConsent } from "@/lib/consent-context";
import { isActiveHref } from "@/lib/active-link";
import { regionFromPathname, regionPath, type Region } from "@/lib/region";
import { resolveLink } from "@/lib/storyblok-link";
import { switchRegion } from "@/lib/switch-region";

const SOCIAL_ICONS: Record<SocialPlatform, React.ReactNode> = {
  instagram: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="currentColor">
      <path d="M7 1.3c1.9 0 2.1 0 2.8.1.7 0 1 .1 1.3.2.3.1.6.3.8.5.3.3.4.5.5.8.1.3.2.6.2 1.3 0 .7.1.9.1 2.8s0 2.1-.1 2.8c0 .7-.1 1-.2 1.3-.1.3-.3.6-.5.8-.3.3-.5.4-.8.5-.3.1-.6.2-1.3.2-.7 0-.9.1-2.8.1s-2.1 0-2.8-.1c-.7 0-1-.1-1.3-.2-.3-.1-.6-.3-.8-.5-.3-.3-.4-.5-.5-.8-.1-.3-.2-.6-.2-1.3 0-.7-.1-.9-.1-2.8s0-2.1.1-2.8c0-.7.1-1 .2-1.3.1-.3.3-.6.5-.8.3-.3.5-.4.8-.5.3-.1.6-.2 1.3-.2.7 0 .9-.1 2.8-.1zM7 0C5.1 0 4.9 0 4.1.1c-.8 0-1.4.2-1.8.4-.5.2-.9.4-1.3.8-.4.4-.6.8-.8 1.3-.2.4-.3 1-.4 1.8C0 4.9 0 5.1 0 7s0 2.1.1 2.9c0 .8.2 1.4.4 1.8.2.5.4.9.8 1.3.4.4.8.6 1.3.8.4.2 1 .3 1.8.4.8 0 1 .1 2.9.1s2.1 0 2.9-.1c.8 0 1.4-.2 1.8-.4.5-.2.9-.4 1.3-.8.4-.4.6-.8.8-1.3.2-.4.3-1 .4-1.8 0-.8.1-1 .1-2.9s0-2.1-.1-2.9c0-.8-.2-1.4-.4-1.8-.2-.5-.4-.9-.8-1.3-.4-.4-.8-.6-1.3-.8-.4-.2-1-.3-1.8-.4C9.1 0 8.9 0 7 0zm0 3.4a3.6 3.6 0 1 0 0 7.2 3.6 3.6 0 0 0 0-7.2zm0 5.9a2.3 2.3 0 1 1 0-4.6 2.3 2.3 0 0 1 0 4.6zm4.6-6a.8.8 0 1 1-1.7 0 .8.8 0 0 1 1.7 0z" />
    </svg>
  ),
  youtube: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="currentColor">
      <path d="M13.7 3.7c-.2-.6-.6-1.1-1.2-1.2C11.4 2.2 7 2.2 7 2.2s-4.4 0-5.5.3c-.6.1-1 .6-1.2 1.2C0 4.8 0 7 0 7s0 2.2.3 3.3c.2.6.6 1.1 1.2 1.2 1.1.3 5.5.3 5.5.3s4.4 0 5.5-.3c.6-.1 1-.6 1.2-1.2.3-1.1.3-3.3.3-3.3s0-2.2-.3-3.3zM5.6 9.1V4.9L9.3 7 5.6 9.1z" />
    </svg>
  ),
  facebook: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="currentColor">
      <path d="M8.5 14V7.5h2.2l.3-2.6H8.5V3.3c0-.7.2-1.2 1.2-1.2h1.3V0H9.2C7.2 0 6 1.2 6 3.1v1.8H4V7.5h2V14h2.5z" />
    </svg>
  ),
  tiktok: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="currentColor">
      <path d="M10 0h-2v9.5c0 .8-.7 1.5-1.5 1.5S5 10.3 5 9.5 5.7 8 6.5 8c.2 0 .3 0 .5.1V6c-.2 0-.3 0-.5 0-1.9 0-3.5 1.6-3.5 3.5S4.6 13 6.5 13 10 11.4 10 9.5V5.3c.7.5 1.6.8 2.5.8V4.1c-1.4 0-2.5-1.1-2.5-2.5V0h0z" />
    </svg>
  ),
  linkedin: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="currentColor">
      <path d="M12.96 0H1.04C.47 0 0 .46 0 1.03v11.93C0 13.53.47 14 1.04 14h11.92c.58 0 1.04-.47 1.04-1.04V1.03C14 .46 13.54 0 12.96 0zM4.15 11.93H2.08V5.27h2.07v6.66zM3.11 4.36c-.66 0-1.2-.54-1.2-1.2 0-.67.54-1.2 1.2-1.2.66 0 1.2.53 1.2 1.2 0 .66-.54 1.2-1.2 1.2zM11.93 11.93h-2.07V8.7c0-.77-.02-1.76-1.07-1.76-1.07 0-1.24.84-1.24 1.7v3.29H5.48V5.27h1.99v.91h.03c.28-.52.95-1.07 1.96-1.07 2.1 0 2.49 1.38 2.49 3.17v3.65z" />
    </svg>
  ),
  x: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="currentColor">
      <path d="M10.79 0h2.13L8.27 5.34l5.48 7.66H9.45L6.08 8.62 2.23 13H.1l4.98-5.71L0 0h4.41l3.05 4.05L10.79 0zm-.75 11.69h1.18L3.99 1.24H2.72l7.32 10.45z" />
    </svg>
  ),
};

const SOCIAL_LABEL: Record<SocialPlatform, string> = {
  instagram: "Instagram",
  youtube: "YouTube",
  facebook: "Facebook",
  tiktok: "TikTok",
  linkedin: "LinkedIn",
  x: "X",
};

const HEADING_CLASS =
  "text-h6 font-semibold leading-5 text-dusk-02";
const SUB_HEADING_CLASS =
  "text-button font-semibold leading-5 text-dusk-02";
const LINK_CLASS =
  "text-button leading-5 text-dusk-03 transition-colors hover:text-white";
const POLICY_CLASS =
  "text-[12px] leading-5 text-dusk-02 transition-colors hover:text-white";

type LinkRow = { key: string; label: string; href: string; external: boolean };

function toLinkRows(items: NavLink[] | undefined, region: Region): LinkRow[] {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      const label = (item.title || "").trim();
      const { href, target } = resolveLink(item.link, region);
      if (!label || href === "#") return null;
      return { key: item._uid, label, href, external: target === "_blank" };
    })
    .filter((row): row is LinkRow => row !== null);
}

function LinkList({ rows }: { rows: LinkRow[] }) {
  const pathname = usePathname();
  if (rows.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1">
      {rows.map((row) => {
        if (row.external) {
          return (
            <li key={row.key}>
              <a
                href={row.href}
                target="_blank"
                rel="noopener noreferrer"
                className={LINK_CLASS}
              >
                {row.label}
              </a>
            </li>
          );
        }
        const active = isActiveHref(row.href, pathname);
        return (
          <li key={row.key}>
            <Link
              href={row.href}
              aria-current={active ? "page" : undefined}
              className={`${LINK_CLASS} inline-flex items-center gap-2`}
            >
              {active && (
                <span
                  aria-hidden
                  className="size-1 shrink-0 rounded-full bg-white"
                />
              )}
              {row.label}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}


function HeadingLink({
  href,
  label,
  rows,
  className,
}: {
  href: string;
  label: string;
  rows?: LinkRow[];
  className?: string;
}) {
  const pathname = usePathname();
  const inList = rows?.some((r) => !r.external && r.href === href) ?? false;
  const active = isActiveHref(href, pathname) && !inList;
  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      className={`inline-flex items-center gap-2 transition-colors hover:text-white${
        className ? ` ${className}` : ""
      }`}
    >
      {active && (
        <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-white" />
      )}
      {label}
    </Link>
  );
}

function ChevronIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path
        d="M3 5l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type Section = {
  id: string;
  heading: string;
  body: React.ReactNode;
  href?: string;
  external?: boolean;
};

// A single-link section links straight to that page instead of opening a
// dropdown, mirroring the header.
function linkSection(id: string, heading: string, rows: LinkRow[]): Section {
  if (rows.length === 1) {
    return { id, heading, body: null, href: rows[0].href, external: rows[0].external };
  }
  return { id, heading, body: <LinkList rows={rows} /> };
}

function MobileAccordion({ sections }: { sections: Section[] }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const pathname = usePathname();
  const headingClass =
    "flex w-full items-center py-4 text-h6 font-semibold leading-normal text-white tracking-[0.02em]";
  return (
    <div>
      {sections.map((s) => {
        if (s.href) {
          return (
            <div key={s.id} className="border-b border-purple-02">
              {s.external ? (
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={headingClass}
                >
                  {s.heading}
                </a>
              ) : (
                <Link
                  href={s.href}
                  aria-current={isActiveHref(s.href, pathname) ? "page" : undefined}
                  className={headingClass}
                >
                  {s.heading}
                </Link>
              )}
            </div>
          );
        }
        const isOpen = openId === s.id;
        return (
          <div key={s.id} className="border-b border-purple-02">
            <button
              type="button"
              onClick={() => setOpenId(isOpen ? null : s.id)}
              aria-expanded={isOpen}
              className="flex w-full cursor-pointer items-center justify-between py-4 text-left"
            >
              <span className="text-h6 font-semibold leading-normal text-white tracking-[0.02em]">
                {s.heading}
              </span>
              <span
                className={`inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-white/24 text-white transition-transform ${isOpen ? "rotate-180" : ""}`}
              >
                <ChevronIcon />
              </span>
            </button>
            <div
              className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
            >
              <div className="overflow-hidden">
                <div className="pb-5 flex flex-col gap-3">{s.body}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ContactBlock({
  email,
  phone,
  socials,
  footnote,
  homeHref,
}: {
  email?: string;
  phone?: string;
  socials: Array<{ _uid: string; platform: SocialPlatform; url: string }>;
  footnote?: string;
  homeHref: string;
}) {
  return (
    <div className="flex h-full flex-col gap-4">
      <Link href={homeHref} aria-label="Thérapie Fertility — home">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/assets/logo/logo-w.svg"
          alt="Thérapie Fertility Logo"
          width={228}
          height={25}
          className="h-[29px] w-auto lg:h-[31px]"
        />
      </Link>
      {(email || phone || socials.length > 0) && (
        <div className="flex flex-col gap-4 pt-5">
          {email && (
            <a
              href={`mailto:${email}`}
              className="text-body font-semibold leading-5 text-white transition-colors hover:text-purple-02"
            >
              {email}
            </a>
          )}
          {phone && (
            <a
              href={`tel:${phone.replace(/\s+/g, "")}`}
              className="text-[17px] font-semibold leading-5 text-white transition-colors hover:text-purple-02"
            >
              {phone}
            </a>
          )}
          {socials.length > 0 && (
            <div className="flex gap-2">
              {socials.map((s) => (
                <a
                  key={s._uid}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={SOCIAL_LABEL[s.platform]}
                  className="flex size-[37px] items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30"
                >
                  {SOCIAL_ICONS[s.platform]}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
      {footnote && (
        <p className="mt-auto max-w-[280px] whitespace-pre-line pt-8 text-button leading-normal text-white/65">
          {footnote}
        </p>
      )}
    </div>
  );
}

function FooterRegionSelector({
  region,
  path,
}: {
  region: Region;
  path: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  const handleTriggerKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      flushSync(() => setOpen(true));
      itemRefs.current[0]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      flushSync(() => setOpen(true));
      const items = itemRefs.current.filter(Boolean) as HTMLButtonElement[];
      items[items.length - 1]?.focus();
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      setOpen(false);
    }
  };

  const handleMenuKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (e.key === "Tab") {
      setOpen(false);
      return;
    }
    const items = itemRefs.current.filter(Boolean) as HTMLButtonElement[];
    if (items.length === 0) return;
    if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
      return;
    }
    if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
      return;
    }
    if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
    e.preventDefault();
    const idx = items.indexOf(e.target as HTMLButtonElement);
    const next =
      e.key === "ArrowDown"
        ? idx < 0
          ? 0
          : (idx + 1) % items.length
        : idx < 0
          ? items.length - 1
          : (idx - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setOpen(false);
    }
  };

  const regions: Array<{ value: Region; name: string }> = [
    { value: "ie", name: "Ireland" },
    { value: "uk", name: "United Kingdom" },
  ];
  const current = regions.find((r) => r.value === region) ?? regions[0];

  return (
    <div
      ref={ref}
      onBlur={handleBlur}
      className="relative mt-auto flex flex-col gap-2 pt-6"
    >
      <span className="text-[12px] font-semibold leading-5 text-dusk-02">
        Region
      </span>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleTriggerKey}
        aria-haspopup="menu"
        aria-expanded={open}
        className="text-button flex w-fit cursor-pointer items-center gap-2 leading-5 text-dusk-03 transition-colors hover:text-white"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={`/assets/region/${current.value}.svg`}
          alt=""
          aria-hidden
          className="size-4 shrink-0 rounded-full object-cover"
        />
        {current.name}
        <span
          className={`text-dusk-04 transition-transform ${open ? "rotate-180" : ""}`}
        >
          <ChevronIcon />
        </span>
      </button>

      {open && (
        <div
          role="menu"
          onKeyDown={handleMenuKey}
          className="absolute bottom-full left-0 mb-2 w-56 rounded-xl bg-white p-1.5 text-midnight shadow-[0_16px_40px_-12px_rgba(0,0,0,0.5)] ring-1 ring-black/5"
        >
          {regions.map((r, i) => (
            <form
              key={r.value}
              action={switchRegion}
              onSubmit={() => setOpen(false)}
            >
              <input type="hidden" name="to" value={r.value} />
              <input type="hidden" name="path" value={path} />
              <button
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="submit"
                role="menuitem"
                tabIndex={-1}
                className={`flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-button transition-colors hover:bg-[#f5f3fa] ${
                  r.value === region
                    ? "bg-[#f5f3fa] font-semibold text-midnight"
                    : "text-midnight/75"
                }`}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={`/assets/region/${r.value}.svg`}
                  alt=""
                  aria-hidden
                  className="size-4 shrink-0 rounded-full object-cover"
                />
                <span className="flex-1 text-left">{r.name}</span>
                {r.value === region && (
                  <Check
                    className="size-4 shrink-0 text-purple-03"
                    strokeWidth={2.5}
                  />
                )}
              </button>
            </form>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Footer({
  navSettings,
}: {
  navSettings: NavSettings | null;
}) {
  const pathname = usePathname();
  // Shared layout isn't re-rendered on region switch; derive from live pathname.
  const region = regionFromPathname(pathname);
  const regionAgnosticPath = pathname.replace(/^\/uk/, "") || "/";
  const year = new Date().getFullYear();
  const { openSettings } = useConsent();
  const homeHref = regionPath("/", region);

  const about = navSettings?.about?.[0];
  const pricing = navSettings?.pricing?.[0];
  const treatments = navSettings?.treatments?.[0];
  const testing = navSettings?.testing?.[0];

  const aboutRows = toLinkRows(about?.about_links, region);
  const pricingRows = toLinkRows(pricing?.links, region);
  const treatmentRows = toLinkRows(treatments?.links, region);
  const womenRows = toLinkRows(testing?.women_links, region);
  const menRows = toLinkRows(testing?.men_links, region);
  const additionalRows = toLinkRows(testing?.additional_links, region);
  const choosingRows = toLinkRows(about?.choosing_links, region);
  // Success Rates has no nav group of its own; place it in the Why Thérapie
  // Fertility list, after Fertility Community.
  const communityIdx = choosingRows.findIndex((r) =>
    r.label.toLowerCase().includes("fertility community"),
  );
  choosingRows.splice(communityIdx === -1 ? choosingRows.length : communityIdx + 1, 0, {
    key: "success-rates",
    label: "Success Rates",
    href: regionPath("/success-rates", region),
    external: false,
  });
  const policyRows = toLinkRows(navSettings?.policy_links, region);
  const socials = (navSettings?.social_links ?? [])
    .filter((s): s is { _uid: string; platform: SocialPlatform; url: string } =>
      Boolean(s.platform && s.url),
    );

  const aboutHeading =
    about?.about_footer_heading || about?.about_heading || "About";
  const pricingHeading =
    pricing?.footer_heading || pricing?.heading || "Pricing";
  const treatmentsHeading =
    treatments?.footer_heading || treatments?.heading || "Treatments";
  const testingHeading =
    testing?.footer_heading || testing?.heading || "Testing";
  const choosingHeading =
    about?.choosing_footer_heading ||
    about?.choosing_heading ||
    "Choosing Thérapie Fertility";
  const womenLabel = testing?.women_label || "For Women";
  const menLabel = testing?.men_label || "For Men";
  const additionalLabel = testing?.additional_label || "Additional";

  const sections: Section[] = [];
  if (aboutRows.length > 0) {
    sections.push(linkSection("about", aboutHeading, aboutRows));
  }
  if (pricingRows.length > 0) {
    sections.push(linkSection("pricing", pricingHeading, pricingRows));
  }
  if (treatmentRows.length > 0) {
    sections.push(linkSection("treatments", treatmentsHeading, treatmentRows));
  }
  if (womenRows.length > 0 || menRows.length > 0 || additionalRows.length > 0) {
    sections.push({
      id: "testing",
      heading: testingHeading,
      body: (
        <>
          {womenRows.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className={SUB_HEADING_CLASS}>{womenLabel}</span>
              <LinkList rows={womenRows} />
            </div>
          )}
          {menRows.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className={SUB_HEADING_CLASS}>{menLabel}</span>
              <LinkList rows={menRows} />
            </div>
          )}
          {additionalRows.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className={SUB_HEADING_CLASS}>{additionalLabel}</span>
              <LinkList rows={additionalRows} />
            </div>
          )}
        </>
      ),
    });
  }
  if (choosingRows.length > 0) {
    sections.push(linkSection("choosing", choosingHeading, choosingRows));
  }

  const HummWordmark = (
    <span
      role="img"
      aria-label="humm"
      className="inline-block h-3 w-[57px] bg-current"
      style={{
        maskImage: "url(/assets/logo/humm-logo-w.svg)",
        WebkitMaskImage: "url(/assets/logo/humm-logo-w.svg)",
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat",
        maskSize: "contain",
        WebkitMaskSize: "contain",
        maskPosition: "center",
        WebkitMaskPosition: "center",
      }}
    />
  );

  const PolicyRow = (
    <div className="flex flex-col gap-1 text-[12px] leading-5 text-dusk-02 md:flex-row md:flex-wrap md:items-center md:gap-x-2 md:gap-y-1">
      <span>©Thérapie Fertility {year}</span>
      {policyRows.length > 0 && (
        <span aria-hidden className="hidden text-dusk-04 md:inline">
          ·
        </span>
      )}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {policyRows.map((row, i) => (
          <span key={row.key} className="flex items-center gap-x-2">
            {i > 0 && <span className="text-dusk-04">|</span>}
            {row.external ? (
              <a
                href={row.href}
                target="_blank"
                rel="noopener noreferrer"
                className={POLICY_CLASS}
              >
                {row.label}
              </a>
            ) : (
              <Link href={row.href} className={POLICY_CLASS}>
                {row.label}
              </Link>
            )}
          </span>
        ))}
        <span className="flex items-center gap-x-2">
          {policyRows.length > 0 && <span className="text-dusk-04">|</span>}
          <button
            type="button"
            onClick={openSettings}
            className={`${POLICY_CLASS} cursor-pointer`}
          >
            Cookie settings
          </button>
        </span>
      </div>
    </div>
  );

  const PaymentsRow = (
    <div className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-[12px] leading-5 text-dusk-03">
      <span>Flexible Payments Plans with</span>
      {HummWordmark}
    </div>
  );

  return (
    <footer className="bg-midnight text-white">
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pt-12 pb-32 md:pb-12 lg:pt-9 lg:pb-9">
        <div className="flex flex-col gap-12 lg:flex-row lg:items-stretch lg:gap-8">
          <div className="lg:w-[320px] lg:shrink-0">
            <ContactBlock
              email={navSettings?.email}
              phone={navSettings?.phone}
              socials={socials}
              footnote={navSettings?.footnote}
              homeHref={homeHref}
            />
          </div>

          <div className="md:hidden">
            <MobileAccordion sections={sections} />
          </div>

          <div className="hidden md:grid md:flex-1 md:grid-cols-4 md:gap-8">
            <div className="flex flex-col gap-5">
              {aboutRows.length > 0 && (
                <div className="flex flex-col gap-4">
                  <h2 className={HEADING_CLASS}>
                    <HeadingLink
                      href={aboutRows[0].href}
                      label={aboutHeading}
                      rows={aboutRows}
                    />
                  </h2>
                  <LinkList rows={aboutRows} />
                </div>
              )}
              {pricingRows.length > 0 && (
                <div className="flex flex-col gap-4 pt-5">
                  <h2 className={HEADING_CLASS}>
                    <HeadingLink
                      href={pricingRows[0].href}
                      label={pricingHeading}
                      rows={pricingRows}
                    />
                  </h2>
                  <LinkList rows={pricingRows} />
                </div>
              )}
            </div>

            {treatmentRows.length > 0 && (
              <div className="flex flex-col gap-4">
                <h2 className={HEADING_CLASS}>
                  <HeadingLink
                    href={regionPath("/treatments", region)}
                    label={treatmentsHeading}
                    rows={treatmentRows}
                  />
                </h2>
                <LinkList rows={treatmentRows} />
              </div>
            )}

            {(womenRows.length > 0 ||
              menRows.length > 0 ||
              additionalRows.length > 0) && (
              <div className="flex flex-col gap-4">
                <h2 className={HEADING_CLASS}>
                  <HeadingLink
                    href={regionPath("/testing", region)}
                    label={testingHeading}
                  />
                </h2>
                {womenRows.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <HeadingLink
                      href={regionPath("/testing/women", region)}
                      label={womenLabel}
                      rows={womenRows}
                      className={SUB_HEADING_CLASS}
                    />
                    <LinkList rows={womenRows} />
                  </div>
                )}
                {menRows.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <HeadingLink
                      href={regionPath("/testing/men", region)}
                      label={menLabel}
                      rows={menRows}
                      className={SUB_HEADING_CLASS}
                    />
                    <LinkList rows={menRows} />
                  </div>
                )}
                {additionalRows.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <span className={SUB_HEADING_CLASS}>{additionalLabel}</span>
                    <LinkList rows={additionalRows} />
                  </div>
                )}
              </div>
            )}

            {choosingRows.length > 0 && (
              <div className="flex flex-col gap-4">
                <h2 className={HEADING_CLASS}>
                  <HeadingLink
                    href={choosingRows[0].href}
                    label={choosingHeading}
                    rows={choosingRows}
                  />
                </h2>
                <LinkList rows={choosingRows} />
                <FooterRegionSelector
                  region={region}
                  path={regionAgnosticPath}
                />
              </div>
            )}
          </div>
        </div>

        <div
          data-footer-legal
          className="mt-16 border-t border-purple-02/30 pt-5 md:mt-12 md:pt-[21px]"
        >
          <div className="flex flex-col gap-8 md:flex-row md:items-center md:justify-between md:gap-6">
            {PolicyRow}
            {PaymentsRow}
          </div>
        </div>
      </div>
    </footer>
  );
}
